// operational-skeleton.jsx — Calm geometry-matching loading primitives.
//
// Replaces ad-hoc `animate-pulse` blocks and raw "Loading…" text
// fallbacks across ChainFlow. Every primitive is intentionally tinted
// against the indigo / slate / glass palette so the placeholders feel
// like *the same surface being hydrated* rather than a different
// component briefly appearing.
//
// Discipline
// ----------
//   • Calm pulse — 2.4 s ease-in-out (slower than the default 2 s
//     Tailwind pulse) so the rhythm reads as composed, not anxious.
//   • Restrained tint — slate/indigo 12 % over the existing panel
//     background; no shimmer, no glow.
//   • Layout stability — every variant ships with explicit width / height
//     so the panel geometry never collapses while hydrating.

import React from "react";

const BASE = {
  background: "linear-gradient(90deg, rgba(148,163,184,0.06) 0%, rgba(148,163,184,0.12) 50%, rgba(148,163,184,0.06) 100%)",
  borderRadius: "8px",
};

const PULSE_STYLE = {
  animation: "chainflow-skeleton-pulse 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite",
};

// One-time global keyframe registration so we don't need to touch
// tailwind config. Calm, restrained, slower than Tailwind's default
// animate-pulse (which is 2 s linear).
if (typeof document !== "undefined" && !document.getElementById("chainflow-skeleton-keyframes")) {
  const style = document.createElement("style");
  style.id = "chainflow-skeleton-keyframes";
  style.textContent = `
    @keyframes chainflow-skeleton-pulse {
      0%, 100% { opacity: 0.55; }
      50%      { opacity: 1; }
    }
    /* Shared calm pulse — apply via the .cf-pulse utility class so
       panel-level loading wrappers across ChainFlow share the same
       restrained 2.4s cadence without per-component overrides. */
    .cf-pulse {
      animation: chainflow-skeleton-pulse 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }
  `;
  document.head.appendChild(style);
}

/** A single calm rectangular placeholder. `w` and `h` are required so
 *  geometry stays stable while hydrating. */
export const SkeletonBlock = ({ w = "100%", h = 12, className = "", testid }) => (
  <div
    className={className}
    style={{ width: w, height: h, ...BASE, ...PULSE_STYLE }}
    data-testid={testid}
    aria-hidden="true"
  />
);

/** Skeleton that exactly matches the HeroKPI geometry in the Director
 *  Centre (icon tile + 2-line stack). Drop-in replacement for the
 *  jarring `"—"` em-dash fallback. */
export const KPISkeleton = ({ testid }) => (
  <div
    className="rounded-xl px-3.5 py-3 flex items-start gap-3"
    style={{
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.08)",
      backdropFilter: "blur(10px)",
    }}
    data-testid={testid}
    aria-hidden="true"
  >
    <div className="w-7 h-7 rounded-md shrink-0" style={{ ...BASE, ...PULSE_STYLE }} />
    <div className="min-w-0 w-full flex flex-col gap-1.5">
      <SkeletonBlock w="55%" h={9} />
      <SkeletonBlock w="80%" h={14} />
    </div>
  </div>
);

/** Skeleton row that matches a typical participant / document list
 *  item (avatar circle + name + meta). */
export const ListRowSkeleton = ({ testid }) => (
  <div
    className="flex items-center gap-3 py-2.5"
    data-testid={testid}
    aria-hidden="true"
  >
    <div className="w-8 h-8 rounded-full shrink-0" style={{ ...BASE, ...PULSE_STYLE }} />
    <div className="flex-1 flex flex-col gap-1.5 min-w-0">
      <SkeletonBlock w="62%" h={11} />
      <SkeletonBlock w="38%" h={9} />
    </div>
  </div>
);

/** Multi-row list skeleton used in Deal Room Documents / Parties. */
export const ListSkeleton = ({ rows = 4, testid }) => (
  <div className="space-y-1" data-testid={testid}>
    {Array.from({ length: rows }).map((_, i) => (
      <ListRowSkeleton key={i} />
    ))}
  </div>
);

/** Generic panel-geometry card skeleton with a calm header bar +
 *  N body lines. Use to replace `<p>Loading…</p>` fallbacks inside
 *  panels that have a known visual rhythm. */
export const CardSkeleton = ({ rows = 3, testid }) => (
  <div className="space-y-2.5" data-testid={testid} aria-hidden="true">
    <SkeletonBlock w="40%" h={11} />
    {Array.from({ length: rows }).map((_, i) => (
      <SkeletonBlock key={i} w={i === rows - 1 ? "65%" : "100%"} h={9} />
    ))}
  </div>
);

/** Drop-in skeleton for a generic modal/drawer body shaped as
 *  (label · field) repeated `fields` times. Mirrors the geometry of
 *  forms in ChaseConfirmDrawer / RequestUpdateSheet so the chrome
 *  stays still while the prefill resolves. */
export const FormFieldsSkeleton = ({ fields = 3, testid }) => (
  <div className="space-y-4" data-testid={testid} aria-hidden="true">
    {Array.from({ length: fields }).map((_, i) => (
      <div key={i} className="space-y-1.5">
        <SkeletonBlock w="22%" h={9} />
        <SkeletonBlock w="100%" h={i === fields - 1 ? 96 : 36} />
      </div>
    ))}
  </div>
);

/** Skeleton row matching a participant-style invite/active card:
 *  rounded-xl with border, avatar circle, two-line text, optional
 *  trailing action chips. Matches ParticipantsModal geometry. */
export const ParticipantRowSkeleton = ({ testid, avatar = 36 }) => (
  <div
    className="rounded-xl border border-slate-200 bg-white p-3.5 flex items-center gap-3"
    data-testid={testid}
    aria-hidden="true"
  >
    <div
      className="rounded-full shrink-0"
      style={{ width: avatar, height: avatar, ...BASE, ...PULSE_STYLE }}
    />
    <div className="flex-1 flex flex-col gap-1.5 min-w-0">
      <SkeletonBlock w="55%" h={11} />
      <SkeletonBlock w="78%" h={9} />
    </div>
    <div className="w-16 h-5 rounded-full shrink-0" style={{ ...BASE, ...PULSE_STYLE }} />
  </div>
);

/** Multi-row participant skeleton — used in Participants Modal pending
 *  & active tabs to preserve the rhythm during fetch. */
export const ParticipantListSkeleton = ({ rows = 3, avatar = 36, testid }) => (
  <div className="space-y-2.5" data-testid={testid}>
    {Array.from({ length: rows }).map((_, i) => (
      <ParticipantRowSkeleton key={i} avatar={avatar} />
    ))}
  </div>
);

/** Canonical Mission Control / Director Centre panel skeleton.
 *  Mirrors the standard panel rhythm: eyebrow icon tile + 2-line
 *  header stack + body card. Two tones — `light` for slate panels
 *  (OperationalMemory, etc.), `dark` for cinematic gradient panels
 *  (Revenue, Communication, Forecast, etc.). Renders the FULL section
 *  geometry so the panel never collapses while data resolves. */
export const MissionPanelSkeleton = ({ tone = "dark", bodyHeight = 168, testid }) => {
  const dark = tone === "dark";
  return (
    <section
      className="mb-7"
      data-testid={testid}
      aria-busy="true"
    >
      {/* Section header strip — matches the populated panel header */}
      <div className="flex items-center gap-2.5 mb-3.5">
        <div
          className="w-7 h-7 rounded-md shrink-0"
          style={{ background: "rgba(148,163,184,0.14)", ...PULSE_STYLE }}
        />
        <div className="min-w-0 flex-1 flex flex-col gap-1.5">
          <SkeletonBlock w="32%" h={9} />
          <SkeletonBlock w="68%" h={11} />
        </div>
        <div
          className="w-20 h-6 rounded-md shrink-0"
          style={{ background: "rgba(148,163,184,0.10)", ...PULSE_STYLE }}
        />
      </div>
      {/* Panel body — calm dark hero or light card */}
      <div
        className="rounded-2xl p-5"
        style={
          dark
            ? {
                background: "linear-gradient(135deg, #0B0F19 0%, #1B1E33 55%, #251A47 100%)",
                border: "1px solid rgba(255,255,255,0.06)",
                height: bodyHeight,
                ...PULSE_STYLE,
              }
            : {
                background: "var(--cf-card)",
                border: "1px solid var(--cf-border)",
                boxShadow: "var(--cf-elev-1)",
                height: bodyHeight,
                ...PULSE_STYLE,
              }
        }
      />
    </section>
  );
};

/** Calm hydration wrapper — short-circuits to a skeleton during
 *  `loading`, otherwise renders children with a 140 ms opacity fade-in
 *  so data arrival never snaps. Drop-in for panels that own a fetch. */
export const HydrationSurface = ({ loading, skeleton, children, testid }) => {
  if (loading) return skeleton;
  return (
    <div className="cf-hydrate-fade" data-testid={testid}>
      {children}
    </div>
  );
};
